import { sortBy } from "lodash-es";
import type { ComputedRef } from "vue";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import type { Art, Team } from "@/model/session";
import { useArtStore } from "@/store/art";
import { isNotUserTeam, useTeamStore } from "@/store/team";

interface TeamSelectorOptions {
  showCurrentTeam?: boolean;
  showOtherArts?: boolean;
  showExternal?: boolean;
}

/**
 * State, data and navigation for the sticky-note TeamSelector.
 *
 * The selector navigates between three views:
 * - "teams": teams of the selected ART (plus an "External orgs" folder for the current ART)
 * - "arts": the list of ARTs to switch between
 * - "externals": the external orgs of the current ART (reached via the folder)
 *
 * Focus management stays in the component (it owns the DOM refs); this composable
 * exposes the writable view state so the component's navigation handlers can drive it.
 */
export function useTeamSelector(options: TeamSelectorOptions) {
  const { t } = useI18n();
  const teamStore = useTeamStore();
  const artStore = useArtStore();

  const view = ref<"teams" | "arts" | "externals">("teams");
  const selectedArt = ref(artStore.current);
  const searchVal = ref("");

  // Drop the current team unless the caller opts to keep it.
  const excludeCurrentTeam = (teams: Team[]) =>
    teams.filter((team) => options.showCurrentTeam || team.id !== teamStore.current.id);

  // Sort comparator: user's own teams first, then alphabetically by name.
  const userThenAlphabetical = [isNotUserTeam, (team: Team) => team.name.toLowerCase()];

  // Teams in the selected ART (excluding externals — those live in their own folder).
  const teamsInCurrentArt: ComputedRef<Team[]> = computed(() =>
    sortBy(excludeCurrentTeam(teamStore.teamsInArt(selectedArt.value.id)), isNotUserTeam),
  );

  // External orgs of the current ART. External teams can exist in multiple ARTs; mirroring to them
  // would be ambiguous as we only have dependent_team_id but not dependent_team_art_id, so they
  // stay tied to the current ART. Sorted alphabetically (a user never belongs to an external team).
  const externals: ComputedRef<Team[]> = computed(() =>
    options.showExternal
      ? sortBy(teamStore.externalsInCurrentArt, (team) => team.name.toLowerCase())
      : [],
  );

  // Externals to show, filtered by the current search.
  const externalsList = computed(() => filterBySearch(externals.value));

  // Show the "External orgs" folder in the team list while viewing the current ART's teams and not
  // searching (during a search, matching externals are surfaced flat in the results instead).
  const showExternalsFolder = computed(
    () =>
      !searchVal.value &&
      externals.value.length > 0 &&
      selectedArt.value.id === artStore.current.id,
  );

  // All regular teams across every ART, plus the current-ART externals. Used for flat cross-ART
  // search, sorted user-teams-first then alphabetically.
  const allTeams: ComputedRef<Team[]> = computed(() =>
    sortBy(excludeCurrentTeam([...teamStore.teams, ...externals.value]), userThenAlphabetical),
  );

  // Teams to show. Without a search, the selected ART's teams (externals live in their folder).
  // While searching, matching externals are surfaced too: cross-ART search spans every ART
  // (allTeams already includes the current-ART externals), single-ART search adds them in.
  const teamsList = computed(() => {
    if (!searchVal.value) return teamsInCurrentArt.value;
    return filterBySearch(
      showArtNav.value ? allTeams.value : [...teamsInCurrentArt.value, ...externals.value],
    );
  });

  // All ARTs
  const arts: ComputedRef<readonly Art[]> = computed(() => artStore.arts || []);

  // ARTs to show (filtered by current search if applicable)
  const artsList = computed(() => filterBySearch(arts.value));

  // Show the 'Other ARTs' button when there is >1 ART
  const showArtNav = computed(() => options.showOtherArts && arts.value.length > 1);

  // Number of results in the active view, surfaced to the search box.
  const resultsCount = computed(() => {
    if (view.value === "arts") return artsList.value?.length;
    if (view.value === "externals") return externalsList.value?.length;
    return teamsList.value?.length;
  });

  // Search input placeholder depends on the active view
  const searchPlaceholder = computed(() => {
    if (view.value === "arts") return t("action.searchArts");
    if (view.value === "externals") return t("action.searchExternalOrgs");
    return t("action.searchTeams");
  });

  /**
   * Filter the given list by the search input (partial match on 'name' property)
   * @param list List of Teams or ARTs
   */
  function filterBySearch<T extends Team | Art>(list: readonly T[]): readonly T[] {
    const input = searchVal.value?.toLowerCase();
    return input ? list.filter((item) => item.name.toLowerCase().includes(input)) : list;
  }

  return {
    view,
    selectedArt,
    searchVal,
    teamsList,
    externalsList,
    artsList,
    showExternalsFolder,
    showArtNav,
    resultsCount,
    searchPlaceholder,
  };
}
